/**
 * 📄 JobOverviewPage
 *
 * 🔹 Central state-holder and controller for the entire Job Listing Page.
 * 🔹 Coordinates all major modules: filtering, searching, sorting, pagination, and preprocessing.
 * 🔹 Acts as the single source of truth for the job data flow.
 *
 * ✅ Responsibilities:
 * - Initializes all modules and links their event-driven callbacks.
 * - Holds both raw (`jobs`) and filtered (`filteredJobs`) job arrays.
 * - Triggers re-rendering of jobs and filters on any state change.
 * - Performs initial preprocessing (e.g. language filtering) on load.
 *
 * 🧠 Internal flow:
 * - Preprocess jobs on load (e.g. remove jobs not matching current language)
 * - Listen for changes from Search, Filters, Sorter, Pagination
 * - Update UI and internal job state accordingly
 *
 * 🚫 Not responsible for:
 * - Direct DOM manipulation beyond its own responsibilities (delegates to FilterUI, PaginationUI, etc.)
 * - Business logic of filtering/sorting/searching (delegated to respective modules)
 *
 * */

import { FilterUI } from "./filterUI";
import { FilterStateManager } from "./filterStateManager";
import { FilterProcessData } from "./filterProcessData";
import { Pagination, PaginationUI } from "./pagination";
import { Search } from "./search";
import { JobSorter, JobSorterUI } from "./jobSorter";
import { JobsPreprocessor } from "./jobsPreprocessor";
import { MapJobUi } from "./mapJobUi";
import { MapLoader } from "./mapLoader";
import { MapGoogle } from "./map"; // Import the Map class
import * as JobTypes from "../types/jobData";

import { FeaturedJobsManager } from "./featured-jobs/featuredJobsManager";

declare const jobData: any;

const siteURL = window.location.origin + window.location.pathname;

// deklare Function initMap
declare global {
  interface Window {
    addMarkers: any;
  }
}

export class JobOverviewPage {
  private jobs: JobTypes.jobData[] = jobData.jobs; // Source of truth 🔥
  private filteredJobs: JobTypes.jobData[] = [...this.jobs]; // ... creates a copy of the array

  private filterProcessDataInstance: FilterProcessData;
  private filterStateManagerInstance: FilterStateManager;
  private filterUIInstance: FilterUI;
  private paginationInstance: Pagination;
  private paginationUI: PaginationUI;
  private searchInstance: Search;
  private jobSorterInstance: JobSorter;

  private JobsPreprocessorInstance: JobsPreprocessor;
  private jobSorterUIInstance: JobSorterUI;

  private isMapLoaded: boolean = false; // Flag to check if map is loaded

  private mapJobUiInstance: MapJobUi;
  private mapInstance: MapGoogle;
  private mapLoaderInstance: MapLoader;

  // Featured Jobs
  private featuredJobsManager: FeaturedJobsManager;

  // translations
  private vertragslaufzeit: string = jobData.vertragslaufzeit;
  private arbeitszeit: string = jobData.arbeitszeit;
  private eintrittstermin: string = jobData.eintrittstermin;
  private kategorie: string = jobData.kategorie;

  // Settings

  static standardShownFilter: number = 5;

  constructor() {
    this.JobsPreprocessorInstance = new JobsPreprocessor();
    this.jobs = this.JobsPreprocessorInstance.returnOnlyCurrentLangJobs(
      this.jobs,
      this.JobsPreprocessorInstance.currentLang
    ); // SINGLE SOURCE OF TRUTH  🔥
    this.filterProcessDataInstance = FilterProcessData.getInstance();
    this.filterStateManagerInstance = new FilterStateManager(this);

    this.filterUIInstance = new FilterUI(this.filterProcessDataInstance, this.filterStateManagerInstance);
    this.paginationInstance = new Pagination(this.jobs, 8);
    this.paginationUI = new PaginationUI(this.paginationInstance);
    this.searchInstance = new Search(this);
    this.featuredJobsManager = new FeaturedJobsManager(this, this.filterStateManagerInstance, this.searchInstance);
    this.featuredJobsManager.initialize();
    this.jobSorterInstance = new JobSorter();
    this.jobSorterUIInstance = new JobSorterUI(this.jobSorterInstance, this);

    this.mapJobUiInstance = null as any; // Initialize to null, will be set in initializeMap
    this.mapInstance = null as any; // Initialize to null, will be set in initializeMap
    this.mapLoaderInstance = null as any; // Initialize to null, will be set in initializeMap

    this.filterProcessDataInstance.initializeAllCategories(this.jobs); // Save all categories for filtering
    this.init();
  }

  public initializeMap() {
    // Initialize the map logic
    if (this.isMapLoaded) {
      console.warn("Map is already loaded. Skipping initialization.");
      return;
    }
    this.mapInstance = new MapGoogle();
    this.mapLoaderInstance = new MapLoader(jobData.googleMapsApiKey, jobData.currentLang);
    this.mapLoaderInstance.load(() => this.mapInstance.init(this));
    this.mapJobUiInstance = new MapJobUi(this);

    this.isMapLoaded = true;
  }

  public get getAllJobs(): JobTypes.jobData[] {
    return this.jobs;
  }

  public get getFilteredJobs(): JobTypes.jobData[] {
    return this.filteredJobs;
  }

  private init() {
    this.updateJobs(this.jobs); // page load initializer 🔥
    this.subscribeToEvents();
    // window.initMap(jobData.jobs);
  }

  // Callbacks
  subscribeToEvents() {
    this.searchInstance.OnSearch((results) => {
      // If Filter are active
      if (this.filterStateManagerInstance.isAFilterActive()) {
        results = this.filterStateManagerInstance.performFilterAndReturn(results);
        console.log("A Filter is active, results:", results);
      }

      this.updateJobs(results);
    });

    this.filterStateManagerInstance.onFilterChange((results) => {
      // check if Search is active
      if (this.searchInstance.isActiveSearch()) {
        results = this.searchInstance.performSearchAndReturn(results);
      }
      this.updateJobs(results);
    });

    this.jobSorterInstance.onSortChange((results) => this.updateJobs(results));
    this.paginationInstance.onPageChange((pageJobs) => this.updateJobDisplay(pageJobs));
  }

  private updateJobs(jobs: JobTypes.jobData[]) {
    this.filteredJobs = jobs;
    jobs = this.jobSorterInstance.performSortReturn(jobs);
    this.filterProcessDataInstance.updateCategoryData(jobs);

    jobs = this.mergeFeaturedJobs(jobs);

    this.paginationInstance.update(jobs);
    this.paginationUI.updatePaginationUI();

    if (this.isMapLoaded) {
      this.mapJobUiInstance.renderJobsOnMapSidebar(jobs); // Update map sidebar with filtered jobs
      this.mapInstance.reloadMarkers(); // Reload markers on map with filtered jobs
    }

    this.updateJobDisplay(this.paginationInstance.getPageJobs());
  }

  /**
   * Adds featured jobs to the job list.
   *
   * @param jobs - Array of job objects
   * @returns
   */
  private mergeFeaturedJobs(jobs: JobTypes.jobData[]): JobTypes.jobData[] {
    // Add featured jobs an own property
    let featuredJobs: JobTypes.jobData[] = [];
    /* Either use featured Jobs search or filter */
    if (this.searchInstance.isActiveSearch()) {
      featuredJobs = this.featuredJobsManager.getFeaturedJobs("search");
    } else {
      featuredJobs = this.featuredJobsManager.getFeaturedJobs("filter");
    }

    console.groupCollapsed("JoboverviewPage.mergeFeaturedJobs");
    console.log("Featured Jobs:", featuredJobs);
    console.log("Jobs:", jobs);
    console.groupEnd();

    let mergedJobs = [];
    if (featuredJobs.length > 0) {
      mergedJobs = [...featuredJobs, ...jobs];
      return this.cleanDuplicates(mergedJobs); // Clean duplicates
    }
    return jobs;
  }

  private cleanDuplicates(jobs: JobTypes.jobData[]): JobTypes.jobData[] {
    const uniqueJobs = new Map<number, JobTypes.jobData>();

    for (const job of jobs) {
      uniqueJobs.set(job.id, job); // überschreibt Duplikate automatisch
    }

    return Array.from(uniqueJobs.values());
  }

  /**
   * Update showed Jobs on Map on Zoom
   *
   * @param jobs
   */
  public updateJobsOnMapSidebar(jobs: JobTypes.jobData[]) {
    this.mapJobUiInstance.renderJobsOnMapSidebar(jobs); // Update map sidebar with filtered jobs
  }

  /* RENDER Functions */

  private updateJobDisplay(jobs: JobTypes.jobData[]) {
    const jobGrid = document.getElementById("job-grid") as HTMLElement;

    // no jobs, hide grid
    if (this.paginationInstance.getPageJobs().length === 0) {
      jobGrid.classList.add("job-grid--empty");
      return;
    }

    // if has class job-grid--empty, remove it
    if (jobGrid.classList.contains("job-grid--empty")) {
      jobGrid.classList.remove("job-grid--empty");
    }

    // render
    jobGrid.innerHTML = this.paginationInstance
      .getPageJobs()
      .map((job) => this.renderCard(job))
      .join("");

    this.filterUIInstance.renderFilters();
  }

  slugify(text: string) {
    return text
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Entferne Diakritika
      .replace(/\(.*?\)/g, "") // ✅ Entferne alles in Klammern inkl. ()
      .replace(/[^a-z0-9]+/g, "-") // Restliche Sonderzeichen zu "-"
      .replace(/^-+|-+$/g, ""); // Trim -
  }

  private renderCard(job: JobTypes.jobData): string {
    const slugPageTitle = job.pageTitle.replace(/\s+/g, "-").toLowerCase();
    const slugJobTitle = this.slugify(slugPageTitle);
    const singlePageUrl = `${siteURL}${slugPageTitle}-${job.id}`;

    /* #region -- Featured Jobs --*/
    let highlightedClass = "";

    if (this.searchInstance.isActiveSearch()) {
      console.log("Current Search Featureds Job IDs:", this.featuredJobsManager.getCurrentFeaturedJobIds("search"));

      if (this.featuredJobsManager.getCurrentFeaturedJobIds("search").includes(job.id)) {
        highlightedClass = "highlighted";
      }
    } else {
      if (this.featuredJobsManager.getCurrentFeaturedJobIds("filter").includes(job.id)) {
        highlightedClass = "highlighted";
      }
    }
    /* #endregion*/

    return /*HTML*/ `
      <li class="job-grid__card ">
      <a href="${siteURL}${job.id}" class="job-grid__card-link ${highlightedClass}"> 
        <div class="job-grid__card-part-one">
          <!-- Title -->
          <span  class="job-grid__card-title"> ${job.pageTitle} </span>
          <!-- Location -->
          <address class="job-grid__card-location">
            ${job.jobOpening.location}
          </address>

                   <!-- Details -->
          <ul class="job-grid__card-info">
            <li class="job-grid__card-info-item">
              <span class="job-grid__card-info-item-label">
                ${this.vertragslaufzeit}: 
              </span>
              <span class="job-grid__card-info-item-value">
                ${job.jobOpening.contractPeriod.name}
              </span>
            </li>
            <li class="job-grid__card-info-item">
              <span class="job-grid__card-info-item-label"> ${this.arbeitszeit}: </span>
              <span class="job-grid__card-info-item-value">
                ${job.jobOpening.workingTimes[0].name}
              </span>
            </li>
            <li class="job-grid__card-info-item">
              <span class="job-grid__card-info-item-label">
                ${this.eintrittstermin}:
              </span>
              <span class="job-grid__card-info-item-value">
                ${job.jobOpening.earliestEntryDate}
              </span>
            </li>
          </ul>
        </div>

        <div class="job-grid__card-part-two">
          <!-- Card Category: -->
          <div class="job-grid__card-category">
            <span class="job-grid__card-category-label"> ${this.kategorie}: </span>
            <span class="job-grid__card-category-value">
                ${this.renderCategories(job.jobOpening.categories)}
            </span>
          </div>
        </div>
        </a> 
      </li>
    `;
  }

  private renderCategories(categories: JobTypes.Category[]) {
    return categories.map((category: JobTypes.Category) => category.name).join(", ");
  }
}
